import type { FC, ReactNode } from 'react'

import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowLeft, ArrowRight, X } from 'lucide-react'
import { gsap, useGSAP } from '@/lib/gsap'
import { prefersReducedMotion } from '@/lib/motion'
import { cn } from '@/lib/utils'
import { getLenis } from '@/components/motion/smooth-scroll'
import { SplitChars } from '@/components/motion/split-text'
import Img from '@/components/generals/img'
import { looks } from '@/constants/content'

/** The nine looks, with a full-screen viewer that steps through them. */
const LooksGrid: FC = (): ReactNode => {
    const viewer = useRef<HTMLDivElement>(null)
    const mounted = useRef(false)
    const [active, setActive] = useState<number | null>(null)
    const [shown, setShown] = useState(0)

    const open = active !== null

    const close = useCallback(() => setActive(null), [])

    const step = useCallback((direction: 1 | -1) => {
        setActive(current => current === null ? current : (current + direction + looks.length) % looks.length)
    }, [])

    useEffect(() => {
        if (active !== null) setShown(active)
    }, [active])

    useGSAP(() => {
        const element = viewer.current

        if (!element) return

        const fast = prefersReducedMotion()

        if (!mounted.current) {
            mounted.current = true
            gsap.set(element, { autoAlpha: 0 })

            return
        }

        if (open) {
            gsap.timeline()
                .to(element, { autoAlpha: 1, duration: fast ? 0 : 0.45, ease: 'power2.out' })
                .fromTo('[data-viewer-frame]', { clipPath: 'inset(100% 0% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: fast ? 0 : 1, ease: 'expo.inOut' }, 0.05)
                .fromTo('[data-viewer-meta]', { autoAlpha: 0, y: 16 }, { autoAlpha: 1, y: 0, duration: fast ? 0 : 0.7, stagger: 0.06 }, 0.4)
        } else {
            gsap.to(element, { autoAlpha: 0, duration: fast ? 0 : 0.4, ease: 'power2.in' })
        }
    }, { scope: viewer, dependencies: [open], revertOnUpdate: false })

    useGSAP(() => {
        if (!open || prefersReducedMotion()) return

        gsap.fromTo('[data-viewer-image]', { autoAlpha: 0, scale: 1.06 }, { autoAlpha: 1, scale: 1, duration: 0.8, ease: 'expo.out' })
    }, { scope: viewer, dependencies: [active], revertOnUpdate: false })

    useEffect(() => {
        if (!open) return

        getLenis()?.stop()

        const onKey = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') close()
            if (event.key === 'ArrowRight') step(1)
            if (event.key === 'ArrowLeft') step(-1)
        }

        window.addEventListener('keydown', onKey)
        viewer.current?.querySelector<HTMLButtonElement>('[data-viewer-close]')?.focus({ preventScroll: true })

        return () => {
            window.removeEventListener('keydown', onKey)

            if (!document.documentElement.classList.contains('is-transitioning')) getLenis()?.start()
        }
    }, [open, close, step])

    const look = looks[active ?? shown]

    return (
        <section id='looks' aria-labelledby='looks-title' className='border-t border-line py-24 gutter md:py-32'>
            <div className='flex flex-wrap items-end justify-between gap-6'>
                <h2 id='looks-title' data-reveal='chars' className='display-l uppercase'>
                    <SplitChars text='Nine looks' />
                </h2>

                <p data-reveal='fade' className='label-mono text-ash'>
                    <span className='hidden md:inline'>Click</span>
                    <span className='md:hidden'>Tap</span>
                    {' '}any look to zoom
                </p>
            </div>

            <ul className='mt-12 grid grid-cols-2 gap-x-3 gap-y-10 md:mt-16 md:grid-cols-3 md:gap-x-6 md:gap-y-14'>
                {looks.map((item, index) => (
                    <li key={item.number} className={cn(index % 3 === 1 && 'md:translate-y-16')}>
                        <button
                            type='button'
                            onClick={() => setActive(index)}
                            data-cursor='Zoom'
                            aria-label={`Zoom look ${item.number}, ${item.chapter}`}
                            className='group block w-full text-left'
                        >
                            <span data-reveal='image' className='relative block aspect-[3/4] overflow-hidden bg-coal'>
                                <Img
                                    src={item.image}
                                    alt={item.alt}
                                    sizes='(min-width: 768px) 32vw, 50vw'
                                    className='transition-transform duration-[1.4s] ease-expo-out group-hover:scale-105'
                                />
                            </span>

                            <span className='mt-3 flex flex-wrap justify-between gap-x-3 meta-mono'>
                                <span className='text-bone'>Look {item.number}</span>
                                <span className='text-ash'>{item.chapter}</span>
                            </span>
                        </button>
                    </li>
                ))}
            </ul>

            <div
                ref={viewer}
                role='dialog'
                aria-modal='true'
                aria-label={`Look ${look.number}`}
                aria-hidden={!open}
                data-lenis-prevent
                className='invisible fixed inset-0 z-[65] flex flex-col bg-ink/95 backdrop-blur-sm'
            >
                <div className='flex items-center justify-between py-5 gutter'>
                    <p data-viewer-meta className='label-mono text-bone'>
                        Look {look.number} <span className='text-ash'>/ 09</span>
                    </p>

                    <button
                        type='button'
                        data-viewer-close
                        onClick={close}
                        tabIndex={open ? 0 : -1}
                        className='flex h-11 items-center gap-3 border border-line px-4 label-mono text-bone transition-colors duration-300 hover:border-bone'
                    >
                        Close

                        <X aria-hidden='true' className='size-4' />
                    </button>
                </div>

                <div className='relative flex min-h-0 flex-1 items-center justify-center px-4 pb-4'>
                    <div data-viewer-frame className='relative aspect-[3/4] h-full max-h-[78vh] max-w-full overflow-hidden bg-coal'>
                        <div data-viewer-image className='absolute inset-0'>
                            <Img src={look.image} alt={look.alt} sizes='(min-width: 768px) 60vh, 90vw' />
                        </div>
                    </div>

                    <button
                        type='button'
                        onClick={() => step(-1)}
                        tabIndex={open ? 0 : -1}
                        aria-label='Previous look'
                        className='absolute left-[var(--gutter)] top-1/2 flex size-12 -translate-y-1/2 items-center justify-center rounded-full border border-line bg-ink/60 text-bone transition-colors duration-300 hover:bg-acid hover:text-ink'
                    >
                        <ArrowLeft aria-hidden='true' className='size-5' />
                    </button>

                    <button
                        type='button'
                        onClick={() => step(1)}
                        tabIndex={open ? 0 : -1}
                        aria-label='Next look'
                        className='absolute right-[var(--gutter)] top-1/2 flex size-12 -translate-y-1/2 items-center justify-center rounded-full border border-line bg-ink/60 text-bone transition-colors duration-300 hover:bg-acid hover:text-ink'
                    >
                        <ArrowRight aria-hidden='true' className='size-5' />
                    </button>
                </div>

                <p data-viewer-meta className='pb-6 text-center meta-mono text-ash gutter'>
                    {look.chapter} — use the arrow keys to step through
                </p>
            </div>
        </section>
    )
}

export default LooksGrid
